// Pickup approval — the counter decides before the guest pays.
// docs/operational-ux-2026-09/07-customer-ordering.md
//
// ─── WHY THIS EXISTS ─────────────────────────────────────────────────────────
//
// Paris Place will not track inventory, and they are right not to: their dishes
// are batch-cooked compounds (rice + sauce + protein + shitto + salad) and a
// recipe model would be a lie within an hour of service. It follows that the
// human at the counter IS the availability oracle — and they will not take a
// stranger's money for food they have not yet confirmed they can make.
//
//     guest submits  →  COUNTER decides  →  guest pays  →  kitchen fires
//
// Toast ships the first half of this (Needs Approval → approve → fires to the
// kitchen), but the guest has usually already paid. Approval BEFORE money is
// ours, and it is the right call for this market: mobile-money reversals in
// Ghana are slow and manual, so a refund costs both sides real time. Not taking
// the money is cheaper than giving it back.
//
// ─── THE LANDMINE THIS MODULE EXISTS TO AVOID ────────────────────────────────
//
// The obvious implementation — flag the new order `requires_prepayment` and let
// the counter release it — is broken, and `lib/order-payment-intent.ts` says why
// at length: `requires_prepayment` makes the accept guard in
// `table-ordering/order-status.ts` REFUSE `accepted`/`preparing` while unpaid
// ("This order is held until the guest pays online"). A request flagged that way
// at submit time could never be accepted, which is the entire feature.
//
// So the two holds run in SEQUENCE, never together:
//
//   SUBMIT   status placed · payment unpaid
//            metadata.awaiting_merchant_approval = true     ← approval hold
//            metadata.requires_prepayment         ABSENT    ← must not be set
//                │  the accept guard is not armed, so ACCEPT is legal ✓
//                ▼
//   ACCEPT   status accepted · payment awaiting_payment
//            awaiting_merchant_approval = false
//            requires_prepayment        = true              ← armed only NOW
//            payment_deadline_at        = now + window
//                │  requires_prepayment now does its documented job:
//                │  it holds the order out of 'preparing' until money lands ✓
//                ▼
//   PAID     payment paid · status preparing — the kitchen fires
//
// Client-safe: imports nothing. Same rule as `lib/order-lifecycle.ts` and
// `lib/order-payment-intent.ts`, so the server, the guest surface and the till
// share one vocabulary. Do not add an import here.

// ─── Policy ──────────────────────────────────────────────────────────────────

export const PICKUP_APPROVAL_MODES = [
  /** Today's behaviour, and the default for every existing branch. Pay, then it fires. */
  'instant',
  /** Paris Place. The counter accepts or declines before the guest is asked for money. */
  'approval',
] as const;

export type PickupApprovalMode = (typeof PICKUP_APPROVAL_MODES)[number];

export const DEFAULT_PICKUP_APPROVAL_MODE: PickupApprovalMode = 'instant';

/**
 * How long an accepted order waits for payment before it expires.
 *
 * Five minutes is long enough for a MoMo PIN prompt on a slow connection, and
 * short enough that the counter is not holding food for someone who wandered
 * off. Configurable per branch; these are the guard rails.
 */
export const DEFAULT_PAYMENT_WINDOW_MINUTES = 5;
export const MIN_PAYMENT_WINDOW_MINUTES = 2;
export const MAX_PAYMENT_WINDOW_MINUTES = 30;

export function isPickupApprovalMode(value: unknown): value is PickupApprovalMode {
  return typeof value === 'string' && (PICKUP_APPROVAL_MODES as readonly string[]).includes(value);
}

export function normalizePickupApprovalMode(value: unknown): PickupApprovalMode {
  return isPickupApprovalMode(value) ? value : DEFAULT_PICKUP_APPROVAL_MODE;
}

export function normalizePaymentWindowMinutes(value: unknown): number {
  // `null` and `''` must mean "not set", not zero. `Number(null)` is 0 and
  // `Number('')` is 0 — both finite — so a bare `Number()` check would clamp an
  // ABSENT setting to the two-minute minimum and hand every guest an impossible
  // payment window. This is the same trap that once turned every NULL numeric
  // column into a real 0 and printed a ticket as "P00".
  if (value === null || value === undefined || value === '') {
    return DEFAULT_PAYMENT_WINDOW_MINUTES;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_PAYMENT_WINDOW_MINUTES;
  return Math.min(Math.max(Math.round(parsed), MIN_PAYMENT_WINDOW_MINUTES), MAX_PAYMENT_WINDOW_MINUTES);
}

// ─── When the money is collected ─────────────────────────────────────────────
//
// ─── WHY THIS IS A SEPARATE AXIS ─────────────────────────────────────────────
//
// `pickup_approval_mode` answers "may the kitchen start?". It used to also
// decide when money moves, because `shouldHoldForApproval` exempted
// `pay_at_counter` — "the guest is paying a person, so the decision and the
// money happen in the same moment". That conflation had a silent consequence
// nobody designed:
//
//   `lib/server/payment-intents.ts` only offered `pay_at_counter` to an
//   order-ahead pickup guest when the branch had NO online rail, so on a
//   cash-only branch the intent was always `pay_at_counter`, so
//   `shouldHoldForApproval` was always false, so turning ON "Confirm orders
//   before payment" did nothing at all. The owner set the toggle, set the
//   payment window, and watched no order get held.
//
// Two questions, two answers:
//
//   approval  — may the kitchen start before money is collected?
//   timing    — when is the money collected?
//
// A branch may hold for approval and still take the cash at the handover, and a
// branch may release straight to the kitchen and still insist on card first.
// Nothing about the answer to one implies the answer to the other.

/**
 * Reuses `PaymentIntent` vocabulary rather than inventing a third one. This file
 * imports nothing (see the header rule), so the values are declared as literals
 * that `lib/order-payment-intent.ts` independently constrains to the same set;
 * `assertPickupPaymentTimingMatchesIntents` in the migration guards the tie.
 */
export const PICKUP_PAYMENT_TIMINGS = ['pay_now', 'pay_at_counter'] as const;

export type PickupPaymentTiming = (typeof PICKUP_PAYMENT_TIMINGS)[number];

export function isPickupPaymentTiming(value: unknown): value is PickupPaymentTiming {
  return typeof value === 'string' && (PICKUP_PAYMENT_TIMINGS as readonly string[]).includes(value);
}

/**
 * Resolve the effective timing.
 *
 * `explicit` is the owner's saved choice. `null` means they have never opened
 * this setting, in which case the pre-existing behaviour is INFERRED from
 * whether the branch can settle online — a branch with no card or mobile-money
 * rail has never been able to take `pay_now`, so offering it would dead-end the
 * guest at checkout.
 *
 * Inferring is a compatibility fallback for un-set rows, NOT the product rule.
 * Once an owner saves the setting the column holds a real value and inference
 * stops, which is what makes the choice visible and stable in the POS.
 */
export function resolvePickupPaymentTiming(
  explicit: unknown,
  canPayOnline: boolean,
): PickupPaymentTiming {
  if (isPickupPaymentTiming(explicit)) return explicit;
  return canPayOnline ? 'pay_now' : 'pay_at_counter';
}

/**
 * Whether accepting under this timing arms the short online payment window.
 *
 * `pay_now` → yes: the guest is on their phone with a MoMo PIN prompt, and the
 * window exists so the counter is not holding a ticket for someone who wandered
 * off.
 *
 * `pay_at_counter` → NO. The cash is collected when the guest physically
 * arrives, which may be hours after acceptance. Arming the 2–30 minute window
 * here would auto-expire every order-ahead guest who accepted at lunchtime and
 * collected at dinner, so this timing deliberately has no deadline. The order is
 * held out of the kitchen by the counter-tender hold (`intentHoldsKitchen` +
 * `confirmCounterPayment`) instead, which is a human-cleared gate rather than a
 * timer.
 */
export function timingArmsPaymentWindow(timing: PickupPaymentTiming): boolean {
  return timing === 'pay_now';
}

// ─── Metadata keys ───────────────────────────────────────────────────────────
//
// Named constants because these strings are read by the guest surface, the till,
// the expiry sweep and the analytics layer. A typo in any one of them is a
// silently-stuck order.

export const APPROVAL_PENDING_KEY = 'awaiting_merchant_approval';
export const PAYMENT_DEADLINE_KEY = 'payment_deadline_at';
export const DECLINE_REASON_KEY = 'decline_reason';
export const DECLINE_NOTE_KEY = 'decline_note';
export const EXPIRY_REASON_KEY = 'expiry_reason';

/**
 * Why the counter turned an order away. Chips, not a free-text box: a reason is
 * data the merchant can count, and "Sold out" is the one that should also offer
 * to 86 the item so the next guest cannot order it either.
 */
export const DECLINE_REASONS = ['sold_out', 'too_busy', 'closing', 'other'] as const;
export type DeclineReason = (typeof DECLINE_REASONS)[number];

export const DECLINE_REASON_LABEL: Record<DeclineReason, string> = {
  sold_out: 'Sold out',
  too_busy: 'Too busy',
  closing: 'Closing soon',
  other: 'Something else',
};

/** What the GUEST is told. Never the engine's word, and never a dead end. */
export const DECLINE_REASON_GUEST_COPY: Record<DeclineReason, string> = {
  sold_out: 'Something on your order just sold out.',
  too_busy: 'The kitchen is at capacity right now.',
  closing: 'They are closing for the day.',
  other: 'They can’t take this order right now.',
};

export function isDeclineReason(value: unknown): value is DeclineReason {
  return typeof value === 'string' && (DECLINE_REASONS as readonly string[]).includes(value);
}

// ─── Reading an order ────────────────────────────────────────────────────────

type OrderMetadata = Record<string, unknown> | null | undefined;

/** True while the counter has not yet accepted or declined this order. */
export function isAwaitingMerchantApproval(metadata: OrderMetadata): boolean {
  return Boolean(metadata && (metadata as Record<string, unknown>)[APPROVAL_PENDING_KEY]);
}

/** The instant an accepted-but-unpaid order stops being held, if it is holding. */
export function readPaymentDeadline(metadata: OrderMetadata): Date | null {
  const raw = metadata ? (metadata as Record<string, unknown>)[PAYMENT_DEADLINE_KEY] : null;
  if (typeof raw !== 'string') return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Whether an accepted order's payment window has closed.
 *
 * Read by the guest surface, the counter board and the sweep, so all three agree
 * without a round trip. Deliberately pure: the caller supplies `now`, so a test
 * does not have to travel in time and two callers in the same request cannot
 * disagree by a millisecond.
 */
export function isPaymentWindowExpired(metadata: OrderMetadata, now: Date = new Date()): boolean {
  const deadline = readPaymentDeadline(metadata);
  return deadline !== null && deadline.getTime() <= now.getTime();
}

/** Seconds left on the countdown, floored at zero. For the guest's timer. */
export function paymentWindowSecondsRemaining(
  metadata: OrderMetadata,
  now: Date = new Date(),
): number | null {
  const deadline = readPaymentDeadline(metadata);
  if (!deadline) return null;
  return Math.max(0, Math.floor((deadline.getTime() - now.getTime()) / 1000));
}

// ─── Building the transitions ────────────────────────────────────────────────

/**
 * The metadata a pickup order is CREATED with under `approval`.
 *
 * Note what is absent: `requires_prepayment`. See the header — setting it here
 * would make the order impossible to accept.
 */
export function buildSubmitHoldMetadata(): Record<string, unknown> {
  return { [APPROVAL_PENDING_KEY]: true };
}

/**
 * The patch applied when the counter ACCEPTS. This is where the payment hold is
 * armed, and only here.
 *
 * ─── Two shapes, chosen by `timing` ───────────────────────────────────────────
 *
 * `pay_now` — the online gate. `requires_prepayment: true` plus a
 * `payment_deadline_at` N minutes out. This is the shape that already existed,
 * unchanged.
 *
 * `pay_at_counter` — deliberately does NOT set `requires_prepayment` and does
 * NOT set a deadline:
 *
 *   • No deadline. The cash is taken when the guest physically arrives, which
 *     may be hours after acceptance. The 2–30 minute window exists to stop the
 *     counter holding a ticket for someone who wandered off mid-PIN-prompt; a
 *     guest who ordered ahead at 11:00 and collects at 19:00 has not wandered
 *     off, and arming the timer would `expired` a perfectly good order.
 *
 *   • No `requires_prepayment`. That flag arms the accept/status guard in
 *     `table-ordering/order-status.ts`, which REFUSES `accepted`/`preparing`
 *     while the order is unpaid and tells staff "held until the guest pays
 *     online". Cash never satisfies an ONLINE prepay guard, so a pay-at-counter
 *     order carrying this flag is stuck permanently — and because the staff
 *     `paid` action maps to `status = 'completed'`, it would be marked COMPLETED
 *     having never been cooked (doc 69 §17).
 *
 *   The hold for this timing is instead expressed as `status: 'accepted'` +
 *   `payment_status: 'awaiting_payment'`, which floats it to the top of the
 *   counter board and is cleared by `confirmCounterPayment` — a human-cleared
 *   gate rather than a timer. That is the "the kitchen does not see this until
 *   the money is in" behaviour the POS promises.
 */
export function buildAcceptPatch(
  windowMinutes: number,
  now: Date = new Date(),
  timing: PickupPaymentTiming = 'pay_now',
): { status: 'accepted'; payment_status: 'awaiting_payment'; metadata: Record<string, unknown> } {
  const metadata: Record<string, unknown> = { [APPROVAL_PENDING_KEY]: false };

  if (timingArmsPaymentWindow(timing)) {
    const minutes = normalizePaymentWindowMinutes(windowMinutes);
    metadata.requires_prepayment = true;
    metadata[PAYMENT_DEADLINE_KEY] = new Date(now.getTime() + minutes * 60_000).toISOString();
  } else {
    metadata.requires_prepayment = false;
  }

  return { status: 'accepted', payment_status: 'awaiting_payment', metadata };
}

/**
 * The patch applied when the counter DECLINES.
 *
 * `cancelled`, not `expired`: both are terminal, but a merchant needs to tell
 * "we said no" from "they never paid", and those are different business problems.
 */
export function buildDeclinePatch(
  reason: DeclineReason,
  note?: string | null,
): { status: 'cancelled'; metadata: Record<string, unknown> } {
  return {
    status: 'cancelled',
    metadata: {
      [APPROVAL_PENDING_KEY]: false,
      [DECLINE_REASON_KEY]: reason,
      ...(note?.trim() ? { [DECLINE_NOTE_KEY]: note.trim() } : {}),
    },
  };
}

/** The patch applied when an accepted order's payment window closes unpaid. */
export function buildExpiryPatch(): { status: 'expired'; metadata: Record<string, unknown> } {
  return {
    status: 'expired',
    metadata: { [EXPIRY_REASON_KEY]: 'payment_window' },
  };
}

/**
 * Whether a pickup order placed under this policy should be held for approval.
 *
 * ─── CHANGED: the `pay_at_counter` exemption is GONE ──────────────────────────
 *
 * It used to read:
 *
 *     if (input.mode !== 'approval') return false;
 *     return input.paymentIntent !== 'pay_at_counter';
 *
 * on the reasoning that a guest paying a person means the counter's decision
 * and the money happen together, so a hold only adds a step (doc 07 §7,
 * decision 3). That reasoning conflated two independent questions, and because
 * `pay_at_counter` was only ever offered to an order-ahead pickup guest when
 * the branch had NO online rail, the exemption was not an edge case — it was
 * every cash-only branch in the product. Turning on "Confirm orders before
 * payment" did nothing there, silently, for every order, forever.
 *
 * `pickup_approval_mode` now means what its label says, for every branch: the
 * counter decides before the kitchen starts. When the money is taken is a
 * separate setting (`pickup_payment_timing`) and is deliberately NOT consulted
 * here.
 *
 * The `paymentIntent` parameter is kept so existing call sites and their tests
 * keep compiling, but it is no longer read. Deleting it is a separate cleanup;
 * leaving it in place is what makes this a behaviour change rather than a
 * signature change.
 */
export function shouldHoldForApproval(input: {
  mode: PickupApprovalMode;
  /** Unused. See the note above — retained only for call-site compatibility. */
  paymentIntent?: string | null;
}): boolean {
  void input.paymentIntent;
  return input.mode === 'approval';
}

// ─── Handing the state to a guest surface ────────────────────────────────────

/**
 * The approval keys, projected out of an order's metadata for the guest.
 *
 * A guest surface needs four facts — is the counter still deciding, when does
 * the payment window close, why was it declined, and was anything added to that
 * reason. It must not be handed the whole `metadata` blob to find them in:
 * that object also carries internal routing state, staff notes and prepay
 * bookkeeping, and a public endpoint that returns it wholesale leaks whatever
 * the next feature puts in there.
 *
 * Returns `null` when the order never went through approval, so a caller can
 * tell "instant branch" from "held and not yet decided" without inspecting keys.
 * The shape is deliberately the metadata's own key names, so
 * `PickupApprovalStatus` and the readers above work on it unchanged — one
 * vocabulary, from the database row to the screen.
 */
export function projectApprovalMetadata(metadata: OrderMetadata): Record<string, unknown> | null {
  if (!metadata) return null;
  const source = metadata as Record<string, unknown>;
  const projected: Record<string, unknown> = {};
  for (const key of [APPROVAL_PENDING_KEY, PAYMENT_DEADLINE_KEY, DECLINE_REASON_KEY, DECLINE_NOTE_KEY, EXPIRY_REASON_KEY]) {
    if (source[key] !== undefined && source[key] !== null) projected[key] = source[key];
  }
  return Object.keys(projected).length > 0 ? projected : null;
}
